import type { PriceList } from '../types/content';

const weekRanges = ['1–3', '4–8', '9–12', '13–16', '17–20', '21–24', '25–30', '31–44'];
const tiers = (prices: number[], currency: string) => weekRanges.map((weeks, i) => ({ weeks, price: `${prices[i]} ${currency}` }));

const tuitionIncludes = [
  'Use of the student portal',
  'All supplementary study materials',
  'Self-access study facilities',
  'Internet access at school',
  'Progress tests',
  'Certificate and end-of-course report',
];

export const priceLists: PriceList[] = [
  {
    slug: 'dubai',
    name: 'Dubai',
    metaTitle: 'Price List Dubai 2026 | English Course Prices | ES World',
    metaDescription: 'ES World Dubai 2026 prices: weekly English course fees from 225 USD, visas, accommodation and other fees. All prices VAT-inclusive.',
    h1: 'Price list Dubai 2026',
    lead: 'Weekly prices for English courses at our Dubai campuses, plus professional courses, visas, accommodation and other fees. The longer you study, the lower your weekly price. All prices are in US dollars and include VAT.',
    currency: 'USD',
    fromPrice: '225 USD',
    validity: 'Valid for courses starting 1 January – 31 December 2026. All prices VAT-inclusive.',
    tierLabels: [],
    rows: [],
    plans: [
      {
        name: 'Semi-intensive',
        lessons: 15,
        lessonLength: '45-minute lessons',
        courses: [
          { name: 'General English' },
          { name: 'Speaking Class' },
          { name: 'IELTS Preparation', note: 'max 6 weeks' },
          { name: 'English for Business', note: 'max 12 weeks' },
          { name: 'English for Academic Purposes', note: 'max 12 weeks' },
          { name: 'English for IELTS', note: 'max 12 weeks' },
        ],
        tiers: tiers([340, 310, 290, 270, 250, 240, 230, 225], 'USD'),
      },
      {
        name: 'Intensive',
        lessons: 30,
        lessonLength: '45-minute lessons',
        courses: [
          { name: 'General English' },
          { name: 'Speaking Class' },
          { name: 'IELTS Preparation', note: 'max 6 weeks' },
          { name: 'English for Business', note: 'max 12 weeks' },
          { name: 'English for Academic Purposes', note: 'max 12 weeks' },
        ],
        tiers: tiers([550, 520, 480, 460, 440, 420, 400, 385], 'USD'),
      },
    ],
    professional: [
      { label: 'CELTA – face-to-face (4 weeks)', price: '2,495 USD' },
      { label: 'CELTA – online (10 weeks)', price: '1,995 USD' },
      { label: 'Digital Marketing (6 weeks, semi-intensive)', price: '1,860 USD' },
      { label: 'Digital Marketing registration', price: '555 USD' },
    ],
    visas: [
      {
        title: 'Applying from outside the UAE',
        groups: [
          { title: 'Student visas', intro: 'Minimum age 16', items: [{ label: '1-year student residence visa', price: '1,365 USD' }, { label: '6-month student residence visa', price: '710 USD' }] },
          { title: 'Tourist & short-stay visas', intro: 'Minimum age 18', items: [{ label: '180-day student visa', price: '500 USD' }, { label: '60-day tourist visa', price: '245 USD' }, { label: '30-day tourist visa', price: '190 USD' }] },
        ],
      },
      {
        title: 'Applying from inside the UAE',
        groups: [
          { title: 'Student visas', intro: 'Minimum age 16', items: [{ label: '1-year student residence visa', price: '1,760 USD' }, { label: '6-month student residence visa', price: '1,235 USD' }] },
          { title: 'Tourist & short-stay visas', intro: 'Minimum age 18', items: [{ label: '180-day student visa', price: '700 USD' }, { label: '30-day tourist visa extension', price: '355 USD' }] },
        ],
        footnote: 'Extensions are only available if your first visa was issued through ES. A 30-day tourist visa can be extended twice at most.',
      },
    ],
    accommodation: [
      {
        title: 'Shared apartment in a 4-star hotel',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/46ae61cb-764a-47be-9307-77a775ad4ce6.jpg',
        note: 'Transport to and from school included. No mixed-gender apartments — book a private apartment instead.',
        columns: ['Weeks', 'Single', 'Shared'],
        rows: [
          { label: '1 to 8', values: ['480 USD', '240 USD'] },
          { label: '9 to 12', values: ['460 USD', '230 USD'] },
          { label: 'From 13', values: ['450 USD', '220 USD'] },
        ],
      },
      {
        title: 'Private apartment in a 4-star hotel',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/d516747b-2a00-437a-b43e-d64fcaa6478b.jpg',
        note: 'Transport to and from school included.',
        columns: ['Weeks', 'Single', 'Shared'],
        rows: [
          { label: '1 to 8', values: ['530 USD', '265 USD'] },
          { label: '9 to 12', values: ['515 USD', '260 USD'] },
          { label: 'From 13', values: ['500 USD', '250 USD'] },
        ],
      },
      {
        title: 'Studio in a premium residence',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/632be5ea-213e-43a8-a5aa-138864f0fe49.jpg',
        note: 'Transport to and from InsuranceMarket metro station included.',
        columns: ['Weeks', 'Single', 'Shared'],
        rows: [
          { label: '1 to 8', values: ['545 USD', '275 USD'] },
          { label: '9 to 12', values: ['525 USD', '265 USD'] },
          { label: 'From 13', values: ['505 USD', '255 USD'] },
        ],
      },
      {
        title: 'Meal plan add-on',
        note: 'Staybridge Al Maktoum only. Meals are served at the Holiday Inn hotel. Breakfast is a hot and cold buffet; half board adds an international dinner buffet.',
        columns: ['Plan', '7 days'],
        rows: [
          { label: 'Breakfast (BB)', values: ['85 USD'] },
          { label: 'Half board (HB)', values: ['195 USD'] },
        ],
      },
    ],
    accommodationIncludes: [
      'Internet and utilities',
      'Gym, pool and common areas',
      'Transport to school (most options)',
      'Breakfast or half board add-on',
      'Weekly bookings from Saturday or Sunday',
    ],
    fees: [
      { label: 'Course registration', price: '115 USD' },
      { label: 'Accommodation booking', price: '110 USD' },
      { label: 'General English book', price: '65 USD' },
      { label: 'Business English book', price: '65 USD' },
      { label: 'IELTS Preparation book', price: '65 USD' },
      { label: 'English for IELTS book (per level)', price: '65 USD' },
      { label: 'Mandatory student insurance (6-month and 1-year visas)', price: '150 USD' },
      { label: 'Airport transfer, one way', price: '110 USD' },
      { label: 'Airport transfer, both ways', price: '195 USD' },
      { label: 'Bank transfer charges', price: '50 USD' },
      { label: 'Credit card charges', price: '3%' },
    ],
    included: tuitionIncludes,
    notes: [
      'Minimum student age is 16.',
      'Accommodation bookings start on Saturday or Sunday, with check-in after 3 pm and check-out before 12 pm. Prices are per person, per week, and subject to availability.',
      'Accommodation includes internet, utilities, use of common areas such as the gym and pool, and transport depending on the provider. Accommodation bookings cannot be paused or frozen.',
      'You can take a two-week holiday in each 12-week term. Tell Student Services at least one week before, or it counts as an absence. Longer breaks count as a course pause, up to 16 weeks in total.',
      'Full course fees are due four weeks before your start date. You can pay by cash, cheque, card, nexpay or bank transfer.',
      'You may be able to move your start date up to 30 days before the original start.',
      'Tell ES about your exit date or any visa change. Overstay fines under UAE law are paid by the student.',
    ],
    holidays: 'Classes run Monday to Friday. The school is closed on UAE public holidays (1 Jan, 20–22 Mar, 26–29 May, 16 Jun, 25 Aug, 1–2 Dec — subject to official announcements) and on 25 December. There are no refunds or make-up classes for these days.',
    faqs: [
      { q: 'Why does the weekly price go down?', a: 'Your weekly price depends on the total length of your booking. For example, 12 weeks of semi-intensive General English is 290 USD a week, while 4 weeks is 310 USD a week.' },
      { q: 'Are prices VAT-inclusive?', a: 'Yes. All Dubai prices include VAT.' },
      { q: 'When do I need to pay?', a: 'Full course fees are due four weeks before your course starts. If payment arrives later, your start date may move.' },
      { q: 'Are there extra charges for card payments?', a: 'Yes. Card and online payments have a 3% surcharge. For bank transfers, the sender pays the bank charges.' },
    ],
  },
  {
    slug: 'london',
    name: 'London',
    metaTitle: 'Price List London 2026 | English Course Prices UK | ES World',
    metaDescription: 'ES World London 2026 prices: weekly English course fees from 245 GBP, accommodation, homestay and other fees.',
    h1: 'Price list London 2026',
    lead: 'Weekly prices for English courses at our Bloomsbury campus, plus accommodation and other fees. The longer you study, the lower your weekly price. All prices are in British pounds.',
    currency: 'GBP',
    fromPrice: '245 GBP',
    validity: 'Valid for courses starting 1 January – 31 December 2026.',
    tierLabels: [],
    rows: [],
    plans: [
      {
        name: 'Semi-intensive',
        lessons: 20,
        lessonLength: '45-minute lessons',
        courses: [
          { name: 'General English' },
          { name: 'Speaking Class' },
          { name: 'IELTS Preparation', note: 'max 12 weeks' },
          { name: 'English for Business', note: 'max 12 weeks' },
          { name: 'Teacher Training', note: 'max 2 weeks, closed groups of 6+' },
        ],
        tiers: tiers([385, 365, 345, 325, 305, 285, 265, 245], 'GBP'),
      },
      {
        name: 'Intensive',
        lessons: 40,
        lessonLength: '45-minute lessons',
        coursesIntro: 'General English combined with one of:',
        courses: [
          { name: 'IELTS Preparation', note: 'max 12 weeks' },
          { name: 'Speaking Class' },
          { name: 'English for Business', note: 'max 12 weeks' },
        ],
        tiers: tiers([575, 550, 520, 490, 460, 430, 400, 370], 'GBP'),
      },
    ],
    accommodation: [
      {
        title: 'ES shared house',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/662068dd-f624-4407-be76-46a0908d8c4e.jpg',
        note: 'Adults 18+. Check-in Sat/Sun 3–9 pm, check-out Sat/Sun before 10 am.',
        columns: ['Room', 'Per week'],
        rows: [
          { label: 'Single room', values: ['305 GBP'] },
          { label: 'Shared room', values: ['225 GBP'] },
        ],
      },
      {
        title: 'Homestay',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/332f6b05-c7b1-4b9e-82c1-24f4b158f4b4.jpg',
        note: 'Check-in 6 am – 10 pm, check-out before 12 pm.',
        columns: ['Room', 'Per week'],
        rows: [
          { label: 'Single room, Zone 2', values: ['305 GBP'] },
          { label: 'Single room, Zone 3', values: ['275 GBP'] },
          { label: 'Single room, Zone 4', values: ['240 GBP'] },
        ],
      },
      {
        title: 'Student residence',
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/d3a1516f-b9b3-4325-a446-0e7cc3aef03d.jpg',
        note: 'Adults 18+. Check-in Sat/Sun 3–9 pm, check-out Sat/Sun before 10 am.',
        columns: ['Room', 'Per week'],
        rows: [{ label: 'Single room, Zone 3', values: ['355 GBP'] }],
      },
      {
        title: 'Supplements & add-ons',
        note: 'Half board is mandatory for students under 18. Supplements may vary by provider.',
        columns: ['Add-on', 'Per week'],
        rows: [
          { label: 'Summer (1 Jun – 31 Aug)', values: ['25 GBP'] },
          { label: 'Junior (under 18)', values: ['30 GBP'] },
          { label: 'Christmas (23 Dec – 4 Jan)', values: ['50 GBP'] },
          { label: 'Private bathroom (Zone 4)', values: ['50 GBP'] },
          { label: 'Halal', values: ['60 GBP'] },
          { label: 'Half board', values: ['50 GBP'] },
        ],
      },
    ],
    accommodationIncludes: [
      'ES shared house, homestay or residence',
      'Rooms in London Zones 2 to 4',
      'Half board available (required under 18)',
      'Halal and private bathroom options',
      'Refundable 150 GBP deposit',
    ],
    fees: [
      { label: 'Course registration', price: '50 GBP' },
      { label: 'Accommodation booking', price: '50 GBP' },
      { label: 'Refundable accommodation deposit (one time)', price: '150 GBP' },
      { label: 'Bedding and kitchen supplement (one time)', price: '50 GBP' },
      { label: 'General English book (per level)', price: '45 GBP' },
      { label: 'IELTS book (per level)', price: '55 GBP' },
      { label: 'Airport transfer, Heathrow (one way)', price: '155 GBP' },
      { label: 'Airport transfer, Gatwick (one way)', price: '165 GBP' },
      { label: 'Airport transfer, Luton, City or Stansted (one way)', price: '160 GBP' },
      { label: 'Bank charges', price: '50 GBP' },
      { label: 'Credit card charges', price: '5%' },
    ],
    included: tuitionIncludes,
    notes: [
      'Minimum student age is 16.',
      'The 150 GBP accommodation deposit is refunded within 60 days of check-out if there is no damage and no rule violations. Bedding and kitchen supplements are non-refundable.',
      'Accommodation rates rise by 5% for bookings on or after 1 January 2027, including 2026 stays that continue into 2027.',
      'You can take a one-week holiday each 12-week term. Tell Student Services at least one week before, or it counts as an absence.',
      'Course pauses depend on your visa conditions. Make sure you finish your course while your visa is valid.',
      'Full course fees are due four weeks before your start date. You can pay by cash, cheque, card, nexpay or bank transfer.',
      'You may be able to move your start date up to 30 days before the original start.',
    ],
    holidays: 'Classes run Monday to Friday. The school is closed on UK public holidays (1 Jan, 3 Apr, 6 Apr, 4 May, 25 May, 31 Aug, 25–26 Dec, 28 Dec — subject to official announcements). There are no refunds or make-up classes for these days.',
    faqs: [
      { q: 'Why does the weekly price go down?', a: 'Your weekly price depends on the total length of your booking. For example, 12 weeks of semi-intensive General English is 345 GBP a week, while 4 weeks is 365 GBP a week.' },
      { q: 'Can I study the intensive course?', a: 'Yes. The 40-lesson intensive option is General English combined with IELTS Preparation, the Speaking Class or English for Business.' },
      { q: 'When do I need to pay?', a: 'Full course fees are due four weeks before your course starts. If payment arrives later, your start date may move.' },
      { q: 'Are there extra charges for card payments?', a: 'Yes. Card and online payments have a 5% surcharge. For bank transfers, the sender pays the bank charges.' },
    ],
  },
  {
    slug: 'camps',
    name: 'Camps',
    metaTitle: 'Price List Camps | English Summer Camp Prices | ES World',
    metaDescription: 'See sample prices for our English summer camps. Lessons, housing, meals and trips in one price. Ask for dates.',
    h1: 'Price list camps: English summer camp prices',
    lead: 'See sample prices for our English summer camps and academic camps. Camp prices include lessons, accommodation, meals, activities and trips. Every price on this page is a sample value, so ask our team for your personal quote.',
    currency: 'Local currency',
    tierLabels: ['1 week', '2 weeks', '4 weeks'],
    rows: [
      { course: 'Language Camp, London', lessons: '15 lessons a week', prices: ['000', '000', '000'] },
      { course: 'Language Camp, New Haven', lessons: '15 lessons a week', prices: ['000', '000', '000'] },
      { course: 'Language Camp, Los Angeles', lessons: '15 lessons a week', prices: ['000', '000', '000'] },
      { course: 'Language Camp, Dubai', lessons: '15 lessons a week', prices: ['000', '000', '000'] },
      { course: 'Academic Camp, London', lessons: '000 lessons a week', prices: ['—', '000', '000'] },
      { course: 'Academic Camp, New Haven', lessons: '000 lessons a week', prices: ['—', '000', '000'] },
    ],
    fees: [
      { label: 'Registration fee (one time)', price: '000' },
      { label: 'Airport transfer (one way)', price: '000' },
      { label: 'Unaccompanied minor service', price: '000' },
      { label: 'Optional extra trip', price: '000' },
    ],
    included: ['English lessons and materials', 'Accommodation and all meals', 'Afternoon and evening activities', 'Weekly full-day trip', 'Care from group leaders day and night'],
    faqs: [
      { q: 'Are these prices final?', a: 'No. These are sample prices only. Our team sends you a personal quote with the final price.' },
      { q: 'Is there a discount for brothers and sisters?', a: 'Ask our team. We will explain any family options for your dates.' },
    ],
  },
];
